import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PhoneCall, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DatePicker, DateTimePicker } from "@/components/DatePicker";

const Ctx = createContext({ trackCall: () => {}, pending: [], openPending: () => {} });
export const useCallTracker = () => useContext(Ctx);
const key = (u) => `omi-pending-calls-${u?.id}`;
const readPending = (u) => { try { return JSON.parse(localStorage.getItem(key(u)) || "[]"); } catch { return []; } };

function ResultForm({ call, outcomes, onSaved, onLater }) {
  const [v, setV] = useState({ outcome: "", remarks: "", next_follow_up: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!v.outcome) return toast.error("Pick a call result");
    setBusy(true);
    try {
      const nfu = v.next_follow_up ? new Date(v.next_follow_up).toISOString() : null;
      await api.post("/workspace/call", { business: call.business, lead_id: call.lead_id, customer_id: call.customer_id, loan_app_id: call.loan_app_id, context: call.context, outcome: v.outcome, remarks: v.remarks, next_follow_up: nfu });
      toast.success("Call result saved"); onSaved();
    } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5" data-testid="call-result-outcomes">
        {outcomes.map((o) => <button key={o} type="button" onClick={() => setV({ ...v, outcome: o })} data-testid={`call-result-outcome-${o.toLowerCase().replace(/\s+/g, "-")}`}
          className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${v.outcome === o ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`}>{o}</button>)}
      </div>
      <div><Label className="text-xs">Next follow-up</Label><DateTimePicker className="mt-1" value={v.next_follow_up} onChange={(x) => setV({ ...v, next_follow_up: x })} testId="call-result-followup" /></div>
      <div><Label className="text-xs">Remarks</Label><Textarea rows={2} className="mt-1" value={v.remarks} onChange={(e) => setV({ ...v, remarks: e.target.value })} data-testid="call-result-remarks" /></div>
      <div className="flex gap-2">
        <Button className="flex-1" onClick={save} disabled={busy} data-testid="call-result-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save result"}</Button>
        <Button variant="outline" onClick={onLater} data-testid="call-result-later">Later</Button>
      </div>
    </div>
  );
}

export function CallTrackerProvider({ children }) {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState(null);
  const [outcomes, setOutcomes] = useState([]);
  const live = useRef(null);
  useEffect(() => { if (user) setPending(readPending(user)); }, [user]);
  const persist = (list) => { setPending(list); localStorage.setItem(key(user), JSON.stringify(list)); };
  const show = useCallback((c) => { setOpen(c); if (!outcomes.length) api.get("/workspace/outcomes").then(({ data }) => setOutcomes(data)).catch(() => {}); }, [outcomes.length]);
  const trackCall = useCallback((info) => { live.current = { ...info, id: `${Date.now()}`, at: new Date().toISOString(), hidden: false }; }, []);
  useEffect(() => {
    const back = () => {
      const c = live.current;
      if (!c || document.visibilityState !== "visible" || !c.hidden) return;
      if (Date.now() - Number(c.id) > 10 * 60 * 1000) { live.current = null; return; }
      live.current = null; show(c);
    };
    const away = () => { if (live.current) live.current.hidden = true; };
    const onVis = () => (document.visibilityState === "hidden" ? away() : back());
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", away);
    window.addEventListener("focus", back);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("blur", away); window.removeEventListener("focus", back); };
  }, [show]);
  const close = (saved) => {
    const c = open; setOpen(null);
    const rest = pending.filter((p) => p.id !== c.id);
    persist(saved ? rest : [c, ...rest].slice(0, 30));
    if (saved) window.dispatchEvent(new Event("omi-call-logged"));
    else toast("Saved to pending call results", { description: "Tap the phone icon at the top to finish it." });
  };
  return (
    <Ctx.Provider value={{ trackCall, pending, openPending: show }}>
      {children}
      <Dialog open={!!open} onOpenChange={(o) => !o && open && close(false)}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-xl" onOpenAutoFocus={(e) => e.preventDefault()} data-testid="call-result-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><PhoneCall className="h-5 w-5 text-primary" />How did the call go?</DialogTitle></DialogHeader>
          {open && <><div className="text-sm text-muted-foreground -mt-2">{open.name} · <span className="font-mono">{open.mobile}</span></div>
            <ResultForm key={open.id} call={open} outcomes={outcomes} onSaved={() => close(true)} onLater={() => close(false)} /></>}
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
}

export function PendingCallsBadge() {
  const { pending, openPending } = useCallTracker();
  if (!pending.length) return null;
  return (
    <Button variant="ghost" size="icon" className="relative text-amber-600" title={`${pending.length} call results pending`} onClick={() => openPending(pending[0])} data-testid="pending-calls-btn">
      <PhoneCall className="h-5 w-5" />
      <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="pending-calls-count">{pending.length}</span>
    </Button>
  );
}
